import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, CalendarPlus, Info, ArrowRight } from 'lucide-react';

const suggestTerms = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-11
  // Suggest the next 3 terms based on where we are in the year
  const all = [
    { label: `Spring ${y}`, order: 0 },
    { label: `Summer ${y}`, order: 1 },
    { label: `Fall ${y}`, order: 2 },
    { label: `Winter ${y}`, order: 3 },
    { label: `Spring ${y + 1}`, order: 4 },
    { label: `Summer ${y + 1}`, order: 5 },
    { label: `Fall ${y + 1}`, order: 6 },
  ];
  const current = m <= 3 ? 0 : m <= 6 ? 1 : m <= 9 ? 2 : 3;
  return all.filter(t => t.order >= current).slice(0, 4).map(t => t.label);
};

const stripTermSuffix = (program) => {
  const title = (program.title || '').trim();
  const termOf = program.term_of;
  if (termOf && title.endsWith(` — ${termOf}`)) {
    return title.slice(0, title.length - ` — ${termOf}`.length).trim();
  }
  return title;
};

/**
 * Copy one or more classes forward into a new term as drafts.
 * `programs` is the full candidate list. When `singleProgram` is given the
 * modal runs in single-class mode with no checkbox list.
 */
export const NextTermModal = ({ open, onClose, token, programs = [], singleProgram = null, onDone }) => {
  const [termLabel, setTermLabel] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => (singleProgram ? [singleProgram.id] : []));
  const [submitting, setSubmitting] = useState(false);

  const suggestions = useMemo(suggestTerms, []);
  const candidates = (singleProgram ? [singleProgram] : programs).filter(p => p.scheduling_mode !== 'by_appointment');

  React.useEffect(() => {
    if (open) {
      setTermLabel('');
      setSelectedIds(singleProgram ? [singleProgram.id] : []);
    }
  }, [open, singleProgram]);

  if (!open) return null;

  const toggle = (id) =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const submit = async () => {
    const label = termLabel.trim();
    if (!label) {
      toast.error('Enter a term name, e.g. "Fall 2026"');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one class to roll forward');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/content/programs/copy-for-term`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ program_ids: selectedIds, term_label: label }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        toast.error(body.detail || 'Could not create the next term');
        return;
      }
      const made = (body.created || []).length;
      if (made > 0) {
        toast.success(`${made} draft class${made === 1 ? '' : 'es'} created for ${label}. Set the dates, then Publish.`);
      }
      (body.skipped || []).forEach(s => toast.warning(s.reason));
      onDone?.();
      onClose();
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full my-8 shadow-xl" data-testid="next-term-modal">
        <div className="flex items-start justify-between p-5 border-b border-stone-200">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 rounded-full bg-cafa-red-100 flex items-center justify-center flex-shrink-0">
              <CalendarPlus size={18} className="text-cafa-red" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-stone-900">
                {singleProgram ? 'Copy this class to the next term' : 'Set up the next term'}
              </h2>
              <p className="text-sm text-stone-500">
                Creates a fresh copy with no students. The current class and its roster stay exactly as they are.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close" data-testid="next-term-close">
            <X size={18} />
          </Button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <Label htmlFor="term-label">Term name</Label>
            <Input
              id="term-label"
              value={termLabel}
              onChange={(e) => setTermLabel(e.target.value)}
              placeholder="Fall 2026"
              className="mt-1"
              data-testid="next-term-label-input"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {suggestions.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setTermLabel(s)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    termLabel === s
                      ? 'bg-cafa-red text-white border-cafa-red'
                      : 'bg-white text-stone-600 border-stone-300 hover:border-cafa-red hover:text-cafa-red'
                  }`}
                  data-testid={`next-term-suggestion-${s.replace(/\s+/g, '-')}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>{singleProgram ? 'Class being copied' : `Classes to roll forward (${selectedIds.length} selected)`}</Label>
            {!singleProgram && candidates.length > 1 && (
              <div className="flex gap-3 mt-1 mb-2">
                <button type="button" className="text-xs text-cafa-red hover:underline"
                  onClick={() => setSelectedIds(candidates.map(c => c.id))} data-testid="next-term-select-all">
                  Select all
                </button>
                <button type="button" className="text-xs text-stone-500 hover:underline"
                  onClick={() => setSelectedIds([])} data-testid="next-term-clear-all">
                  Clear
                </button>
              </div>
            )}
            <div className="mt-1 border border-stone-200 rounded-md divide-y divide-stone-100 max-h-64 overflow-y-auto">
              {candidates.length === 0 && (
                <p className="p-4 text-sm text-stone-500">No classes available to copy.</p>
              )}
              {candidates.map(p => {
                const base = stripTermSuffix(p);
                const checked = selectedIds.includes(p.id);
                return (
                  <label
                    key={p.id}
                    className={`flex items-start gap-3 p-3 cursor-pointer ${checked ? 'bg-cafa-red-50' : 'hover:bg-stone-50'}`}
                    data-testid={`next-term-option-${p.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(p.id)}
                      disabled={!!singleProgram}
                      className="mt-1 h-4 w-4 accent-[#800020]"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-stone-800 truncate">{base}</span>
                      <span className="block text-xs text-stone-500 truncate">
                        {p.instructor}{p.term_of ? ` • currently ${p.term_of}` : ''}
                      </span>
                      {checked && termLabel.trim() && (
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-cafa-red">
                          <ArrowRight size={12} /> {base} — {termLabel.trim()}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p><strong>Carried over:</strong> title, instructor, description, photo, prices, capacity, age range/level, location and materials.</p>
              <p><strong>Not carried over:</strong> start and end dates, registrations, payments, attendance, archive status and the student roster — the new term starts empty.</p>
              <p>Each copy is saved as a <strong>Draft</strong> and stays hidden from the website until you review the dates, meeting times, prices and capacity, then hit <strong>Publish</strong>.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-5 border-t border-stone-200">
          <Button
            onClick={submit}
            disabled={submitting || !termLabel.trim() || selectedIds.length === 0}
            className="bg-cafa-red hover:bg-cafa-red-900 text-white"
            data-testid="next-term-confirm"
          >
            <CalendarPlus size={15} className="mr-1.5" />
            {submitting
              ? 'Creating…'
              : `Create ${selectedIds.length || ''} draft${selectedIds.length === 1 ? '' : 's'}`}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="next-term-cancel">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
